import { useContext, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { ReactComponent as DownArrow } from '../../../assets/images/downarrowcolored.svg';
import { ReactComponent as UpArrow } from '../../../assets/images/uparrowcolored.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/images/deleteAlertIcon.svg';
import { ReactComponent as AddIcon } from '../../../assets/images/CircleAdd.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/infoIcon.svg';
import {
  AlertColor,
  Grid,
  Paper,
  Table,
  TableBody,
  TableContainer,
} from '@mui/material';

import { Formik, FormikProps, useFormikContext } from 'formik';
import * as yup from 'yup';
import dayjs, { Dayjs } from 'dayjs';
import AutocompleteSearch from '../../../components/formlib/AutocompleteSearch';
import DatePickerComponent from '../../../components/formlib/DatePickerComponent';
import MediumTypography from '../../../components/formlib/MediumTypography';
import ModalPopup from '../../../components/formlib/ModalPopup';
import { HeadCell, TableHeader } from '../../../components/formlib/TableHeader';
import { LookUpContext } from '../../../context/LookUpContextProvider';
import {
  addOrUpdateIfspHealthAndEligibilitySummaryReport,
  deleteEvaluatonTeamMember,
  deleteIfspHealthAndEligibilitySummary,
  EmployeeListType,
  getIfspHealthAndEligibilitySummaryReport,
} from '../../../services/ifspClient/IfspApi';
import { formatName } from '../../../utils/nameUtils';
import InformationModal from '../../ifsp/InformationModal';
import { OptionType } from '../../../utils/type';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import HealthEligibilityTableRow from './reportComponents/HealthEligibilityTableRow';
import CommentTextbox from '../../../components/formlib/CommentTextbox';
import { LoaderContext, LoaderContextType } from '../../../layouts/AppSidebar';
import SnackBarComponent from '../../../components/formlib/SnackBarComponent';
import { useIfspReportValidationCheckToSubmit } from './reportComponents/ifspReportTabValidationCheck';
import {
  EvaluationTeamMemberType,
  setIfspHealthSummaryReport,
  updateIfspHealthSummaryReport,
  updateOriginalIfspHealthSummaryReport,
} from '../../../redux/features/ifsp/ifspHealthSummarySlice';
import ButtonComponent from '../../../components/formlib/ButtonComponent';
import { useIfspUnsavedChanges } from '../ifspUnsavedChanges';
import IfspHeld from '../ifspComponents/IfspHeld';
import LazyAutocompleteSearch from '../../../components/formlib/LazyAutocompleteSearch';
import { getAllEmployeeByName } from '../../../services/configApi/employees/employeeServices';
import { updateIfspUnsavedFields } from '../../../redux/features/ifsp/ifspUnsavedFieldSlice';
import { v4 as uuidv4 } from 'uuid';

interface TableDataType {
  evaluationName: string;
  evaluationDiscipline: string;
  action: string;
}

const headCells: HeadCell<TableDataType>[] = [
  {
    id: 'evaluationName',
    labelId: 'Evaluation Name',
    defaultLabelId: 'Evaluation Team Member Name',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'evaluationDiscipline',
    labelId: 'Evaluation Discipline',
    defaultLabelId: 'Evaluation Team Member Discipline',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'action',
    labelId: 'Action',
    defaultLabelId: 'Action',
    numeric: false,
    requiredSorting: false,
  },
];

const validationSchema = yup.object().shape({
  teamMemberName: yup.string().required('IFSP.EvaluationName'),
  teamMemberDiscipline: yup.string().required('IFSP.EvaluationDiscipline'),
});

const HealthEligibilitySummaryReport = () => {
  const ifspHealthSummaryReport = useAppSelector(
    (state) => state.ifspHealthSummaryReportDetails.ifspHealthSummaryReport,
  );
  const ifspOriginalHealthSummaryReport = useAppSelector(
    (state) =>
      state.ifspHealthSummaryReportDetails.originalIfspHealthSummaryReport,
  );

  const ifspPermission = useAppSelector((state) => state.ifspPermissionDetails);

  const ifspReportFormValidation = useIfspReportValidationCheckToSubmit(
    ifspHealthSummaryReport,
  );

  const ifspUnsavedFields = useAppSelector((state) => state.ifspUnsavedFields);
  const dispatch = useAppDispatch();

  const [isEdit, setIsEdit] = useState(false);
  const [editEvaluationId, setEditEvaluationId] = useState<string>('');
  const [disciplineList, setDisciplineList] = useState<OptionType[]>([]);
  const [deleteTeamMemberIds, setDeleteTeamMemberIds] = useState<{
    teamMemberId: string;
    id: string;
    uiUuid: string;
  }>({
    teamMemberId: '',
    id: '',
    uiUuid: '',
  });
  const [
    duplicateEvaluationDisciplineError,
    setDuplicateEvaluationDisciplineError,
  ] = useState<boolean>(false);
  const [deleteWarning, setDeleteWarning] = useState<boolean>(false);
  const [deleteNewSection, setDeleteNewSection] = useState<boolean>(false);
  const [openAccordianIndex, setOpenAccordianIndex] = useState<number>(0);
  const [openAccordian, setOpenAccordian] = useState<boolean>(false);
  const [transitionRenderKey, setTransitionRenderKey] = useState(0);
  const [open, setOpen] = useState<boolean>(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');
  const [infoModal, setInfoModal] = useState(false);
  const [employeeList, setEmployeeList] = useState<EmployeeListType[]>([]);
  const [openUnSavedModal, setOpenUnSavedModal] = useState<boolean>(false);
  const [isSaveClicked, setIsSaveClicked] = useState<boolean>(false);
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;

  const parentRef = useRef<HTMLDivElement>(null);
  const { lookups } = useContext(LookUpContext);

  const formikRef = useRef<FormikProps<EvaluationTeamMemberType>>(null);
  const { isHealthSummaryChanged, isHealthSummaryChangedAndNoCreatedDate } =
    useIfspUnsavedChanges();
  const ifspHeatBeatDetails = useAppSelector(
    (state) => state.ifspHeatBeatDetails,
  );

  useEffect(() => {
    toggleLoader(true);
    handleGetIfspHealthEligibilityReport();
  }, [ifspHeatBeatDetails.healthEligibilityEmpId]);

  const MemberFormUnsavedWatcher = () => {
    const { values } = useFormikContext<EvaluationTeamMemberType>();

    useEffect(() => {
      const hasChanges = Object.entries(values)
        .filter(([key]) => key !== 'memberType')
        .some(([, value]) => {
          if (typeof value === 'object' && value !== null) {
            return Object.values(value).some(Boolean);
          }

          return Boolean(value);
        });

      dispatch(
        updateIfspUnsavedFields({
          ...ifspUnsavedFields,
          isHealthAndEligibilityFieldsChanged: hasChanges,
        }),
      );
    }, [values, dispatch]);

    return null;
  };

  const handleGetIfspHealthEligibilityReport = () => {
    getIfspHealthAndEligibilitySummaryReport()
      .then((response) => {
        if (response) {
          const healthSummary = [...response.healthSummary];

          if (
            healthSummary.length > 0 &&
            !healthSummary[0].eligibilityDateEvaluation &&
            response.latestEvaluationDate
          ) {
            healthSummary[0] = {
              ...healthSummary[0],
              eligibilityDateEvaluation: response.latestEvaluationDate,
            };
          }

          const formattedResponse = {
            ...response,
            healthSummary,
          };

          toggleLoader(false);
          dispatch(setIfspHealthSummaryReport(formattedResponse));
        }
      })
      .catch(() => {
        setOpen(true);
        setToastrVariable('error');
        setToastrId('IFSP.HealthEligibilitySummary.FetchError');
        setToastrDefaultMessage('Failed to fetch health eligibility summary');
      })
      .finally(() => {
        toggleLoader(false);
      });
  };

  const sanitizeSearch = (text: string) => {
    if (!text) return '';

    if (text.includes(',')) {
      const [last, first] = text.split(',');
      return `${first?.trim()} ${last?.trim()}`;
    }

    return text;
  };

  const fetchEmployees = (
    search: string,
    page: number,
  ): Promise<OptionType[]> => {
    const cleanSearch = sanitizeSearch(search);
    const healthSummaryEmployeeIds = ifspHealthSummaryReport.healthSummary[
      openAccordianIndex
    ].evaluationTeamMembers.map((member) => member.teamMemberId || '');
    return getAllEmployeeByName(
      cleanSearch,
      page,
      10,
      page === 0 ? healthSummaryEmployeeIds : [],
    )
      .then((data) => {
        toggleLoader(false);
        setEmployeeList((prev) => (page === 0 ? data : [...prev, ...data]));
        return data.map((emp) => ({
          id: emp.id,
          label: `${emp.lastName}, ${emp.firstName}`,
        }));
      })
      .catch((err) => {
        toggleLoader(false);
        setToastrVariable('error');
        setToastrId('IFSP.failedToGetEmployeeData');
        setToastrDefaultMessage(err);

        return [];
      });
  };

  const addEvaluationdata = (values: EvaluationTeamMemberType) => {
    const evaluationTeamMembers =
      ifspHealthSummaryReport.healthSummary[openAccordianIndex]
        .evaluationTeamMembers;

    const membersForDuplicateCheck = isEdit
      ? evaluationTeamMembers.filter((member) => member.id !== editEvaluationId)
      : evaluationTeamMembers;

    const isDuplicated = membersForDuplicateCheck.some(
      (evaluation) =>
        evaluation.teamMemberId === values.teamMemberId &&
        evaluation.teamMemberDiscipline === values.teamMemberDiscipline,
    );
    if (isDuplicated) {
      setDuplicateEvaluationDisciplineError(true);
      return;
    }
    if (isEdit) {
      const update = ifspHealthSummaryReport.healthSummary[
        openAccordianIndex
      ].evaluationTeamMembers.map((member) =>
        member.id === editEvaluationId ? values : member,
      );
      dispatch(
        updateIfspHealthSummaryReport({
          ...ifspHealthSummaryReport,
          healthSummary: ifspHealthSummaryReport.healthSummary.map(
            (item, index) => {
              if (index === openAccordianIndex) {
                return {
                  ...item,
                  evaluationTeamMembers: update,
                };
              }
              return item;
            },
          ),
        }),
      );
      setIsEdit(false);
      setEditEvaluationId('');
    } else {
      dispatch(
        updateIfspHealthSummaryReport({
          ...ifspHealthSummaryReport,
          healthSummary: ifspHealthSummaryReport.healthSummary.map(
            (item, index) => {
              if (index === openAccordianIndex) {
                return {
                  ...item,
                  evaluationTeamMembers: [
                    ...item.evaluationTeamMembers,
                    values,
                  ],
                };
              }
              return item;
            },
          ),
        }),
      );
    }
  };

  const editEvaluationData = (data: EvaluationTeamMemberType) => {
    const discipline = employeeList
      .filter((employee) => employee.id === data.teamMemberId)
      .flatMap((employee) =>
        (employee.disciplineHistoryList || []).map((history) => ({
          id: history.disciplineType ?? '',
          label:
            lookups?.employeeDiscipline.find(
              (dis) => dis.code === history.disciplineType,
            )?.description ?? '',
        })),
      );

    const uniqueDiscipline = Array.from(
      new Map(discipline.map((d) => [d.id, d])).values(),
    );

    if (uniqueDiscipline.length > 0) {
      setDisciplineList(uniqueDiscipline);
    } else {
      setDisciplineList([]);
    }

    setIsEdit(true);
    setEditEvaluationId(data.id ?? '');
    formikRef.current?.setValues(data);
  };
  const handledeleteEvaluationTeamMemberDetails = (
    data: EvaluationTeamMemberType,
  ) => {
    setDeleteTeamMemberIds({
      teamMemberId: data.teamMemberId ?? '',
      id: data.id ?? '',
      uiUuid: data.uiUuid ?? '',
    });
    setDeleteWarning(true);
  };

  const handledeleteEvaluationTeamMember = () => {
    toggleLoader(true);
    if (deleteTeamMemberIds.id) {
      deleteEvaluatonTeamMember(deleteTeamMemberIds.id)
        .then(() => {
          setOpen(true);
          setToastrVariable('success');
          setToastrId('IFSP.HealthEligibility.DeleteSuccess');
          setToastrDefaultMessage(
            'Evaluation team member deleted successfully',
          );
          dispatch(
            updateIfspHealthSummaryReport({
              ...ifspHealthSummaryReport,
              healthSummary: ifspHealthSummaryReport.healthSummary.map(
                (item, index) => {
                  if (index === openAccordianIndex) {
                    return {
                      ...item,
                      evaluationTeamMembers: item.evaluationTeamMembers.filter(
                        (member) => member.id !== deleteTeamMemberIds.id,
                      ),
                    };
                  }
                  return item;
                },
              ),
            }),
          );
          dispatch(
            updateOriginalIfspHealthSummaryReport({
              ...ifspOriginalHealthSummaryReport,
              healthSummary: ifspOriginalHealthSummaryReport.healthSummary.map(
                (item, index) => {
                  if (index === openAccordianIndex) {
                    return {
                      ...item,
                      evaluationTeamMembers: item.evaluationTeamMembers.filter(
                        (member) => member.id !== deleteTeamMemberIds.id,
                      ),
                    };
                  }
                  return item;
                },
              ),
            }),
          );
        })
        .catch(() => {
          setOpen(true);
          setToastrVariable('error');
          setToastrId('IFSP.HealthEligibility.DeleteError');
          setToastrDefaultMessage('Failed to delete evaluation team member');
        })
        .finally(() => {
          setDeleteWarning(false);
          toggleLoader(false);
        });
    } else if (deleteTeamMemberIds.uiUuid) {
      dispatch(
        updateIfspHealthSummaryReport({
          ...ifspHealthSummaryReport,
          healthSummary: ifspHealthSummaryReport.healthSummary.map(
            (item, index) => {
              if (index === openAccordianIndex) {
                return {
                  ...item,
                  evaluationTeamMembers: item.evaluationTeamMembers.filter(
                    (member) => member.uiUuid !== deleteTeamMemberIds.uiUuid,
                  ),
                };
              }
              return item;
            },
          ),
        }),
      );
      toggleLoader(false);
      setDeleteWarning(false);
    }
  };

  const createNewSection = () => {
    dispatch(
      updateIfspHealthSummaryReport({
        ...ifspHealthSummaryReport,
        healthSummary: [
          ...ifspHealthSummaryReport.healthSummary,
          {
            id: '',
            additionalInformationComment: null,
            currentHealthComment: null,
            healthHistoryComment: null,
            eligibilityDateEvaluation: null,
            eligibilityEvaluationResultComment: null,
            evaluationTeamMembers: [],
            sessionReviewedDate: null,
          },
        ],
      }),
    );
    dispatch(
      updateOriginalIfspHealthSummaryReport({
        ...ifspOriginalHealthSummaryReport,
        healthSummary: [
          ...ifspOriginalHealthSummaryReport.healthSummary,
          {
            id: '',
            additionalInformationComment: null,
            currentHealthComment: null,
            healthHistoryComment: null,
            eligibilityDateEvaluation: null,
            eligibilityEvaluationResultComment: null,
            evaluationTeamMembers: [],
            sessionReviewedDate: null,
          },
        ],
      }),
    );
  };
  const deleteSection = () => {
    toggleLoader(true);
    const summaryId =
      ifspHealthSummaryReport.healthSummary[
        ifspHealthSummaryReport.healthSummary.length - 1
      ].id;
    if (summaryId) {
      setOpen(true);
      setToastrVariable('success');
      setToastrId('IFSP.HealthEligibility.DeleteSuccess');
      setToastrDefaultMessage(
        'Health and Eligibility Summary deleted successfully',
      );
      deleteIfspHealthAndEligibilitySummary(summaryId)
        .then(() => {
          dispatch(
            updateOriginalIfspHealthSummaryReport({
              healthSummary:
                ifspOriginalHealthSummaryReport.healthSummary.slice(0, -1),
            }),
          );
          dispatch(
            updateIfspHealthSummaryReport({
              ...ifspHealthSummaryReport,
              healthSummary: ifspHealthSummaryReport.healthSummary.slice(0, -1),
            }),
          );
        })
        .catch(() => {
          setOpen(true);
          setToastrVariable('error');
          setToastrId('IFSP.HealthEligibility.DeleteError');
          setToastrDefaultMessage(
            'Failed to delete Health and Eligibility Summary',
          );
        })
        .finally(() => {
          setDeleteNewSection(false);
          toggleLoader(false);
        });
    } else {
      dispatch(
        updateIfspHealthSummaryReport({
          ...ifspHealthSummaryReport,
          healthSummary: ifspHealthSummaryReport.healthSummary.slice(0, -1),
        }),
      );
      dispatch(
        updateOriginalIfspHealthSummaryReport({
          ...ifspOriginalHealthSummaryReport,
          healthSummary: ifspOriginalHealthSummaryReport.healthSummary.slice(
            0,
            -1,
          ),
        }),
      );
      setDeleteNewSection(false);
      toggleLoader(false);
    }
  };

  const handleCloseSnakBar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const handleOpenInfoModal = () => {
    setInfoModal(true);
  };

  const handleCloseInfoModal = () => {
    setInfoModal(false);
  };

  const canShowDeleteSection = (mainIndex: number): boolean => {
    const lastIndex = ifspHealthSummaryReport.healthSummary.length - 1;
    const lastItem = ifspHealthSummaryReport.healthSummary[lastIndex];

    return (
      ifspPermission.hasClientEditPermission &&
      !lastItem.sessionReviewedDate &&
      mainIndex === lastIndex &&
      mainIndex > 0
    );
  };

  const addOrUpdateIfspReportDetails = () => {
    if (Object.keys(ifspReportFormValidation).length > 0) {
      setIsSaveClicked(true);
      setOpen(true);
      setToastrVariable('error');
      setToastrId('IFSP.Report.UpdateError');
      setToastrDefaultMessage(
        'Please correct the highlighted fields before submitting.',
      );
      setTimeout(() => {
        setOpen(false);
      }, 3000);
      return;
    } else {
      setIsSaveClicked(false);
    }
    toggleLoader(true);
    addOrUpdateIfspHealthAndEligibilitySummaryReport({
      ...ifspHealthSummaryReport,
      healthSummary: ifspHealthSummaryReport.healthSummary.map(
        (item, index) => {
          const isLastIndex =
            ifspHealthSummaryReport.healthSummary.length - 1 === index;
          return {
            ...item,
            sessionReviewedDate:
              isLastIndex &&
              isHealthSummaryChangedAndNoCreatedDate &&
              !item.sessionReviewedDate
                ? dayjs().format('MM/DD/YYYY hh:mm A')
                : item.sessionReviewedDate,
          };
        },
      ),
    })
      .then(() => {
        setOpen(true);
        setToastrVariable('success');
        setToastrId('IFSP.ReportDetails.UpdateSuccess');
        setToastrDefaultMessage('IFSP report updated successfully');
        return handleGetIfspHealthEligibilityReport();
      })
      .catch(() => {
        setOpen(true);
        setToastrVariable('error');
        setToastrId('IFSP.ReportDetails.UpdateError');
        setToastrDefaultMessage('Failed to update IFSP report');
      })
      .finally(() => toggleLoader(false));
  };

  return (
    <Box className="ml-md mr-md mb-md">
      {ifspHealthSummaryReport.isCurrentlyHeld ? (
        <Box sx={{ position: 'relative' }}>
          <IfspHeld ifspHealBy={ifspHealthSummaryReport?.heldBy} />
        </Box>
      ) : null}
      <Box
        ref={parentRef}
        style={{
          pointerEvents: ifspHealthSummaryReport.isCurrentlyHeld
            ? 'none'
            : 'auto',
          opacity: ifspHealthSummaryReport.isCurrentlyHeld ? '0.5' : '1 ',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onKeyDownCapture={(e) => {
          if (ifspHealthSummaryReport.isCurrentlyHeld) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onFocus={() => {
          if (ifspHealthSummaryReport.isCurrentlyHeld) {
            if (parentRef.current) parentRef.current.focus();
          }
        }}
      >
        <Box
          sx={{
            border: '1px solid #008C82',
            borderBottomLeftRadius: '4px',
            borderBottomRightRadius: '4px',
            borderTop: 'none',
          }}
          className="p-sm"
        >
          <Formik
            initialValues={{
              id: '',
              uiUuid: '',
              teamMemberId: '',
              teamMemberName: '',
              teamMemberDiscipline: '',
            }}
            innerRef={formikRef}
            validationSchema={validationSchema}
            onSubmit={(values, { resetForm }) => {
              resetForm();
              setDisciplineList([]);
              values.uiUuid = values.uiUuid || uuidv4();
              addEvaluationdata(values);
            }}
          >
            {({
              values,
              setFieldValue,
              setValues,
              errors,
              touched,
              handleSubmit,
              resetForm,
            }) => (
              <Box component="main">
                <MemberFormUnsavedWatcher />
                <SnackBarComponent
                  open={open}
                  handleClose={handleCloseSnakBar}
                  successOrError={toastrVariable}
                  labelId={toastrId}
                  defaultMessageId={toastrDefaultMessage}
                />
                <InformationModal
                  open={infoModal}
                  titleLabel="IFSP.Information"
                  titleDefaultLabel="Information"
                  buttonLabel="IFSP.InfoOkButton"
                  buttonDefaultLabel="Ok"
                  onClose={handleCloseInfoModal}
                  infoLabelId="IFSP.HealthEligibilitySummaryInfo"
                  infoDefaultLabel="This page documents information gathered during the evaluation and assessment process and reflects the child's eligibility for Early Intervention. Included here are statements regarding child's medical history and current health status as well as other pertinent information that the family chooses to share."
                />
                <ModalPopup
                  open={duplicateEvaluationDisciplineError}
                  description={'duplicateEvalDisciplineWarningMessage'}
                  onOk={() => {
                    setDuplicateEvaluationDisciplineError(false);
                    setIsEdit(false);
                    setEditEvaluationId('');
                  }}
                  labelId2="Clientpage.Okbtn"
                  positiveActionLabel="Ok"
                />
                <ModalPopup
                  open={deleteWarning}
                  description="permanentDeleteConfirmationMessage"
                  onOk={() => {
                    handledeleteEvaluationTeamMember();
                  }}
                  onCancel={() => {
                    setDeleteWarning(false);
                  }}
                  labelId1="Clientpage.cancelbtn"
                  negativeActionLabel="cancelText"
                  labelId2="Clientpage.Okbtn"
                  positiveActionLabel="Ok"
                />
                <ModalPopup
                  open={openUnSavedModal}
                  description="UnsavedAlertDescriptionWhileLeaving"
                  labelId1="Clientpage.cancelbtn"
                  negativeActionLabel="Cancel"
                  labelId2="Clientpage.Okbtn"
                  positiveActionLabel="Ok"
                  onCancel={() => {
                    setOpenUnSavedModal(false);
                  }}
                  onOk={() => {
                    dispatch(
                      setIfspHealthSummaryReport(
                        ifspOriginalHealthSummaryReport,
                      ),
                    );
                    resetForm();
                    setTransitionRenderKey((prev) => prev + 1);
                    setTimeout(() => {
                      setOpenUnSavedModal(false);
                    }, 500);
                  }}
                />
                <ModalPopup
                  open={deleteNewSection}
                  description="permanentDeleteConfirmationMessage"
                  onOk={() => {
                    deleteSection();
                  }}
                  onCancel={() => {
                    setDeleteNewSection(false);
                  }}
                  labelId1="Clientpage.cancelbtn"
                  negativeActionLabel="cancelText"
                  labelId2="Clientpage.Okbtn"
                  positiveActionLabel="Ok"
                />
                {ifspHealthSummaryReport.healthSummary.map(
                  (ifspHealthSummaryData, mainIndex) => (
                    <Box
                      key={ifspHealthSummaryData.id}
                      component="section"
                      className="p-lg borderRadius4 mb-lg"
                      sx={{
                        border:
                          isSaveClicked &&
                          ifspReportFormValidation.dateOfEligibilityEvaluationErrorIndex?.includes(
                            mainIndex,
                          )
                            ? '1px solid red'
                            : '1px solid #00C6B8',
                        backgroundColor: '#ECF9F8',
                      }}
                    >
                      <Box
                        component="div"
                        className="flex__justify__space-between"
                      >
                        {openAccordian && mainIndex === openAccordianIndex ? (
                          <Box
                            component="div"
                            className="flex__justify__space-between"
                          >
                            <MediumTypography
                              labelid="IFSP.session2"
                              defaultlabel="Health & Eligibility Summary"
                              fontSize="18px"
                              fontweight={600}
                              textColor="#2A4241"
                              className="pr-md"
                            />
                            <InfoIcon
                              style={{ alignSelf: 'center' }}
                              className="cursorPointer"
                              onClick={handleOpenInfoModal}
                            />
                            {ifspHealthSummaryReport.healthSummary[mainIndex]
                              .sessionReviewedDate ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  padding: '4px 8px 4px 8px',
                                  borderRadius: '4px',
                                  marginLeft: '24px',
                                  backgroundColor: '#97A6A5',
                                  alignItems: 'center',
                                }}
                              >
                                <MediumTypography
                                  labelid="IFSP.UpdatedOn"
                                  defaultlabel="Created On: "
                                  fontSize="14px"
                                  fontweight={400}
                                  textColor="#fff"
                                />
                                <MediumTypography
                                  fontSize="14px"
                                  fontweight={400}
                                  textColor="#fff"
                                  className="pl-xs"
                                  label={
                                    ifspHealthSummaryReport.healthSummary[
                                      mainIndex
                                    ].sessionReviewedDate ?? ''
                                  }
                                />
                              </Box>
                            ) : null}
                          </Box>
                        ) : (
                          <Box className="flex__justify__space-between">
                            <MediumTypography
                              labelid="IFSP.session2"
                              defaultlabel="Health & Eligibility Summary"
                              fontSize="18px"
                              fontweight={600}
                              textColor="#2A4241"
                            />
                            {ifspHealthSummaryReport.healthSummary[mainIndex]
                              .sessionReviewedDate ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  padding: '4px 8px 4px 8px',
                                  borderRadius: '4px',
                                  marginLeft: '24px',
                                  backgroundColor: '#97A6A5',
                                  alignItems: 'center',
                                }}
                              >
                                <MediumTypography
                                  labelid="IFSP.UpdatedOn"
                                  defaultlabel="Created On: "
                                  fontSize="14px"
                                  fontweight={400}
                                  textColor="#fff"
                                />
                                <MediumTypography
                                  fontSize="14px"
                                  fontweight={400}
                                  textColor="#fff"
                                  className="pl-xs"
                                  label={
                                    ifspHealthSummaryData.sessionReviewedDate as string
                                  }
                                />
                              </Box>
                            ) : null}
                          </Box>
                        )}
                        {openAccordian && mainIndex === openAccordianIndex ? (
                          <Box className="flex__ align__items__center">
                            {ifspPermission.hasClientEditPermission &&
                            ifspHealthSummaryReport.healthSummary[
                              ifspHealthSummaryReport.healthSummary.length - 1
                            ].sessionReviewedDate &&
                            ifspHealthSummaryReport.healthSummary.length - 1 ===
                              mainIndex ? (
                              <Box
                                className="cursorPointer"
                                sx={{
                                  padding: '4px 8px 4px 8px',
                                  borderRadius: '4px',
                                  marginRight: '24px',
                                  border: '1px solid #008C82',
                                  backgroundColor: '#fff',
                                }}
                                onClick={() => {
                                  createNewSection();
                                }}
                              >
                                <MediumTypography
                                  labelid="IFSP.AddNewSection"
                                  defaultlabel="+ Add New"
                                  fontSize="12px"
                                  fontweight={600}
                                />
                              </Box>
                            ) : (
                              canShowDeleteSection(mainIndex) && (
                                <Box
                                  component={'div'}
                                  className="cursorPointer flex__ align__items__center pr-md"
                                  onClick={() => {
                                    setDeleteNewSection(true);
                                  }}
                                  sx={{
                                    padding: '4px 8px 4px 8px',
                                    borderRadius: '4px',
                                    marginRight: '24px',
                                    border: '1px solid #EB4C60',
                                    backgroundColor: '#fff',
                                  }}
                                >
                                  <DeleteIcon className="pr-xs" />
                                  <MediumTypography
                                    labelid="IFSP.DeleteNewSection"
                                    defaultlabel="Delete"
                                    fontSize="12px"
                                    fontweight={600}
                                  />
                                </Box>
                              )
                            )}
                            <UpArrow
                              className="cursorPointer"
                              onClick={() => {
                                formikRef.current?.setTouched({}, false);
                                setOpenAccordianIndex((prev) => prev);
                                setOpenAccordian(false);
                              }}
                            />
                          </Box>
                        ) : (
                          <Box className="flex__ align__items__center">
                            {ifspPermission.hasClientEditPermission &&
                            ifspHealthSummaryReport.healthSummary[
                              ifspHealthSummaryReport.healthSummary.length - 1
                            ].sessionReviewedDate &&
                            ifspHealthSummaryReport.healthSummary.length - 1 ===
                              mainIndex ? (
                              <Box
                                className="cursorPointer"
                                sx={{
                                  padding: '4px 8px 4px 8px',
                                  borderRadius: '4px',
                                  marginRight: '24px',
                                  border: '1px solid #008C82',
                                  backgroundColor: '#fff',
                                }}
                                onClick={() => {
                                  createNewSection();
                                }}
                              >
                                <MediumTypography
                                  labelid="IFSP.AddNewSection"
                                  defaultlabel="+ Add New"
                                  fontSize="12px"
                                  fontweight={600}
                                />
                              </Box>
                            ) : (
                              canShowDeleteSection(mainIndex) && (
                                <Box
                                  component={'div'}
                                  className="cursorPointer flex__ align__items__center pr-md"
                                  onClick={() => {
                                    setDeleteNewSection(true);
                                  }}
                                  sx={{
                                    padding: '4px 8px 4px 8px',
                                    borderRadius: '4px',
                                    marginRight: '24px',
                                    border: '1px solid #EB4C60',
                                    backgroundColor: '#fff',
                                  }}
                                >
                                  <DeleteIcon className="pr-xs" />
                                  <MediumTypography
                                    labelid="IFSP.DeleteNewSection"
                                    defaultlabel="Delete"
                                    fontSize="12px"
                                    fontweight={600}
                                  />
                                </Box>
                              )
                            )}
                            <DownArrow
                              className="cursorPointer"
                              onClick={() => {
                                formikRef.current?.setTouched({}, false);
                                setOpenAccordianIndex(mainIndex);
                                setOpenAccordian(true);
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                      {openAccordian && mainIndex === openAccordianIndex && (
                        <Box
                          component="div"
                          className="mt-lg"
                          key={transitionRenderKey}
                        >
                          <Box
                            ref={parentRef}
                            style={{
                              pointerEvents:
                                !ifspPermission.hasClientEditPermission
                                  ? 'none'
                                  : 'auto',
                              opacity: !ifspPermission.hasClientEditPermission
                                ? '0.5'
                                : '1 ',
                            }}
                            onKeyDownCapture={(e) => {
                              if (!ifspPermission.hasClientEditPermission) {
                                e.preventDefault();
                                e.stopPropagation();
                              }
                            }}
                            onFocus={() => {
                              if (!ifspPermission.hasClientEditPermission) {
                                if (parentRef.current)
                                  parentRef.current.focus();
                              }
                            }}
                          >
                            <Grid container xs={12}>
                              <Grid item xs={4.5} className="pr-lg">
                                <DatePickerComponent
                                  className="bg__white"
                                  labelid="IFSP.eligibilityEvaluationDate"
                                  disableFuture={false}
                                  defaultlabelid="Date of Eligibility Evaluation"
                                  value={
                                    ifspHealthSummaryReport.healthSummary[
                                      openAccordianIndex
                                    ].eligibilityDateEvaluation
                                      ? dayjs(
                                          ifspHealthSummaryReport.healthSummary[
                                            openAccordianIndex
                                          ].eligibilityDateEvaluation,
                                        )
                                      : null
                                  }
                                  handlechange={(dateValue: Dayjs | null) => {
                                    if (dateValue) {
                                      const formattedDate =
                                        dayjs(dateValue).format('MM/DD/YYYY');
                                      dispatch(
                                        updateIfspHealthSummaryReport({
                                          ...ifspHealthSummaryReport,
                                          healthSummary:
                                            ifspHealthSummaryReport.healthSummary.map(
                                              (item, index) => {
                                                if (
                                                  index === openAccordianIndex
                                                ) {
                                                  return {
                                                    ...item,
                                                    eligibilityDateEvaluation:
                                                      formattedDate,
                                                  };
                                                }
                                                return item;
                                              },
                                            ),
                                        }),
                                      );
                                    } else {
                                      dispatch(
                                        updateIfspHealthSummaryReport({
                                          ...ifspHealthSummaryReport,
                                          healthSummary:
                                            ifspHealthSummaryReport.healthSummary.map(
                                              (item, index) => {
                                                if (
                                                  index === openAccordianIndex
                                                ) {
                                                  return {
                                                    ...item,
                                                    eligibilityDateEvaluation:
                                                      null,
                                                  };
                                                }
                                                return item;
                                              },
                                            ),
                                        }),
                                      );
                                    }
                                  }}
                                />
                                {isSaveClicked &&
                                  ifspReportFormValidation.dateOfEligibilityEvaluationErrorIndex?.includes(
                                    mainIndex,
                                  ) && (
                                    <MediumTypography
                                      className="errorText-md"
                                      labelid={
                                        ifspReportFormValidation.dateOfEligibilityEvaluationErrorMessageId
                                      }
                                      defaultlabel={
                                        ifspReportFormValidation.dateOfEligibilityEvaluationErrorMessage
                                      }
                                    />
                                  )}
                              </Grid>
                            </Grid>
                            <Grid container className="mt-lg">
                              <Grid
                                item
                                xs={4.5}
                                className="pr-lg"
                                display={'none'}
                              >
                                <AutocompleteSearch
                                  className="bg__white"
                                  labelId="IFSP.evaluationTeamMemberName"
                                  defaultId="Evaluation Team Member Name"
                                  Required
                                  data={employeeList
                                    .filter(
                                      (employee) =>
                                        !ifspHealthSummaryReport.healthSummary[
                                          openAccordianIndex
                                        ].evaluationTeamMembers.some(
                                          (teamMember) =>
                                            teamMember.teamMemberId ===
                                            employee.id,
                                        ),
                                    )
                                    .map((employee) => ({
                                      id: employee.id ?? '',
                                      label: employee.name
                                        ? employee.lastName +
                                          ', ' +
                                          employee.firstName
                                        : '',
                                    }))}
                                  selectedValue={{
                                    id:
                                      employeeList.find(
                                        (employee) =>
                                          employee.id === values.teamMemberId,
                                      )?.id ?? '',
                                    label: formatName(
                                      employeeList.find(
                                        (employee) =>
                                          employee.id === values.teamMemberId,
                                      )?.firstName ?? '',
                                      null,
                                      employeeList.find(
                                        (employee) =>
                                          employee.id === values.teamMemberId,
                                      )?.lastName ?? '',
                                      null,
                                    ),
                                  }}
                                  onSelectItem={(data) => {
                                    if (data) {
                                      setValues((value) => ({
                                        ...value,
                                        teamMemberName: data.label,
                                        teamMemberId: data.id,
                                        teamMemberDiscipline: '',
                                      }));
                                      const discipline = employeeList
                                        .filter(
                                          (employee) =>
                                            employee.id === data.id &&
                                            employee.disciplineType,
                                        )
                                        .map((employee) => ({
                                          id: employee.disciplineType ?? '',
                                          label:
                                            lookups?.employeeDiscipline.find(
                                              (dis) =>
                                                dis.code ===
                                                employee.disciplineType,
                                            )?.description ?? '',
                                        }));
                                      if (discipline.length > 0) {
                                        setDisciplineList(discipline);
                                      } else {
                                        setDisciplineList([]);
                                      }
                                    } else {
                                      setValues((value) => ({
                                        ...value,
                                        teamMemberName: '',
                                        teamMemberId: '',
                                        teamMemberDiscipline: '',
                                      }));
                                      setDisciplineList([]);
                                    }
                                  }}
                                  disableLabel={false}
                                />
                                {errors.teamMemberName &&
                                  touched.teamMemberName && (
                                    <MediumTypography
                                      className="errorText-md"
                                      labelid={errors.teamMemberName}
                                      defaultlabel="Evaluation team member is required"
                                    />
                                  )}
                              </Grid>
                              <Grid item xs={4.5} className="pr-lg">
                                <LazyAutocompleteSearch
                                  labelId="IFSP.evaluationTeamMemberName"
                                  defaultId="Evaluation Team Member Name"
                                  value={
                                    employeeList.find(
                                      (employee) =>
                                        employee.id === values.teamMemberId,
                                    )
                                      ? {
                                          id: employeeList.find(
                                            (employee) =>
                                              employee.id ===
                                              values.teamMemberId,
                                          )!.id,
                                          label: formatName(
                                            employeeList.find(
                                              (employee) =>
                                                employee.id ===
                                                values.teamMemberId,
                                            )!.firstName,
                                            null,
                                            employeeList.find(
                                              (employee) =>
                                                employee.id ===
                                                values.teamMemberId,
                                            )!.lastName,
                                            null,
                                          ),
                                        }
                                      : null
                                  }
                                  onChange={(data) => {
                                    if (data) {
                                      setValues((value) => ({
                                        ...value,
                                        teamMemberName: data.label,
                                        teamMemberId: data.id,
                                        teamMemberDiscipline: '',
                                      }));
                                      const discipline = employeeList
                                        .filter(
                                          (employee) => employee.id === data.id,
                                        )
                                        .flatMap(
                                          (employee) =>
                                            employee.disciplineHistoryList?.map(
                                              (history) => ({
                                                id:
                                                  history.disciplineType ?? '',
                                                label:
                                                  lookups?.employeeDiscipline.find(
                                                    (dis) =>
                                                      dis.code ===
                                                      history.disciplineType,
                                                  )?.description ?? '',
                                              }),
                                            ) || [],
                                        );

                                      const uniqueDiscipline = Array.from(
                                        new Map(
                                          discipline.map((d) => [d.id, d]),
                                        ).values(),
                                      );

                                      if (uniqueDiscipline.length > 0) {
                                        setDisciplineList(uniqueDiscipline);
                                      } else {
                                        setDisciplineList([]);
                                      }
                                    } else {
                                      setValues((value) => ({
                                        ...value,
                                        teamMemberName: '',
                                        teamMemberId: '',
                                        teamMemberDiscipline: '',
                                      }));
                                      setDisciplineList([]);
                                    }
                                  }}
                                  fetchOptions={fetchEmployees}
                                  required
                                />
                                {errors.teamMemberName &&
                                  touched.teamMemberName && (
                                    <MediumTypography
                                      className="errorText-md"
                                      labelid={errors.teamMemberName}
                                      defaultlabel="Evaluation team member is required"
                                    />
                                  )}
                              </Grid>
                              <Grid
                                item
                                xs={4.5}
                                className="align__items__center"
                              >
                                <AutocompleteSearch
                                  className="bg__white"
                                  labelId="IFSP.evaluationTeamMemberDiscipline"
                                  defaultId="Evaluation Team Member Discipline"
                                  Required
                                  data={disciplineList}
                                  selectedValue={{
                                    id: values.teamMemberDiscipline ?? '',
                                    label:
                                      disciplineList.find(
                                        (dis) =>
                                          dis.id ===
                                          values.teamMemberDiscipline,
                                      )?.label ?? '',
                                  }}
                                  onSelectItem={(data) => {
                                    if (data) {
                                      setFieldValue(
                                        'teamMemberDiscipline',
                                        data.id,
                                      );
                                    } else {
                                      setFieldValue('teamMemberDiscipline', '');
                                    }
                                  }}
                                  disableLabel={false}
                                />
                                {errors.teamMemberDiscipline &&
                                  touched.teamMemberDiscipline && (
                                    <MediumTypography
                                      className="errorText-md"
                                      labelid={errors.teamMemberDiscipline}
                                      defaultlabel="Evaluation team member discipline is required"
                                    />
                                  )}
                              </Grid>
                              <Grid item xs={2}>
                                <Box
                                  component="div"
                                  className="cursorPointer flex__ ml-lg mt-xs"
                                  onClick={() => handleSubmit()}
                                >
                                  <AddIcon className="mr-sm" />
                                  <MediumTypography
                                    labelid={
                                      isEdit ? 'IFSP.Update' : 'IFSP.Add'
                                    }
                                    defaultlabel={isEdit ? 'Update' : 'Add'}
                                    fontweight={400}
                                    textColor="#2A4241"
                                  />
                                </Box>
                              </Grid>
                            </Grid>
                            <Box component="div" className="mt-md">
                              <TableContainer component={Paper}>
                                <Table>
                                  <TableHeader
                                    className="listDataTableHead"
                                    headerNames={headCells}
                                    checkBoxRequired={false}
                                  />
                                  <TableBody className="tableRowcss">
                                    {ifspHealthSummaryReport.healthSummary[
                                      mainIndex
                                    ].evaluationTeamMembers.map(
                                      (listData: EvaluationTeamMemberType) => (
                                        <HealthEligibilityTableRow
                                          key={`${listData.teamMemberId}-${listData.teamMemberDiscipline}-${listData.id}`}
                                          employeeList={employeeList}
                                          data={listData}
                                          onEdit={(data) => {
                                            editEvaluationData(data);
                                          }}
                                          onDelete={(data) => {
                                            handledeleteEvaluationTeamMemberDetails(
                                              data,
                                            );
                                          }}
                                        />
                                      ),
                                    )}
                                  </TableBody>
                                </Table>
                                {ifspHealthSummaryReport.healthSummary[
                                  mainIndex
                                ].evaluationTeamMembers.length === 0 && (
                                  <Box component="div" className=" width__100">
                                    <MediumTypography
                                      textstyle={'center'}
                                      labelid="IFSP.emptyMessage"
                                      defaultlabel="No data added yet"
                                      className="p-sm"
                                    />
                                  </Box>
                                )}
                              </TableContainer>
                            </Box>

                            <Box
                              sx={{
                                alignItems: 'center',
                                display: 'inline-flex',
                              }}
                              className="mt-md"
                            >
                              <MediumTypography
                                sxProps={{
                                  marginLeft: '4px',
                                  marginRight: '24px',
                                  fontFamily: 'Lato-Regular',
                                  fontSize: 14,
                                  fontWeight: 500,
                                }}
                                labelid={'IFSP.EligibilityEvaluationResults'}
                                defaultlabel={'Eligibility Evaluation Results:'}
                              />
                            </Box>
                            <Box sx={{ paddingTop: '8px' }}>
                              <CommentTextbox
                                className="translate"
                                name={'concern'}
                                placeholder={'Enter your comments'}
                                rows={5}
                                maxLength={5000}
                                Value={
                                  ifspHealthSummaryReport.healthSummary[
                                    mainIndex
                                  ].eligibilityEvaluationResultComment
                                }
                                handlechange={(value) => {
                                  dispatch(
                                    updateIfspHealthSummaryReport({
                                      ...ifspHealthSummaryReport,
                                      healthSummary:
                                        ifspHealthSummaryReport.healthSummary.map(
                                          (item, index) => {
                                            if (index === mainIndex) {
                                              return {
                                                ...item,
                                                eligibilityEvaluationResultComment:
                                                  value || null,
                                              };
                                            }
                                            return item;
                                          },
                                        ),
                                    }),
                                  );
                                }}
                                style={{
                                  width: '840px',
                                }}
                              />
                            </Box>

                            <Box
                              sx={{
                                alignItems: 'center',
                                display: 'inline-flex',
                              }}
                            >
                              <MediumTypography
                                sxProps={{
                                  marginLeft: '4px',
                                  marginRight: '24px',
                                  fontFamily: 'Lato-Regular',
                                  fontSize: 14,
                                  fontWeight: 500,
                                }}
                                labelid={'IFSP.HealthHistory'}
                                defaultlabel={
                                  "Health History: (include child's previous health and medical experiences)"
                                }
                              />
                            </Box>
                            <Box sx={{ paddingTop: '8px' }}>
                              <CommentTextbox
                                className="translate"
                                name={'concern'}
                                placeholder={'Enter your comments'}
                                rows={5}
                                maxLength={5000}
                                Value={
                                  ifspHealthSummaryReport.healthSummary[
                                    mainIndex
                                  ].healthHistoryComment
                                }
                                handlechange={(value) => {
                                  dispatch(
                                    updateIfspHealthSummaryReport({
                                      ...ifspHealthSummaryReport,
                                      healthSummary:
                                        ifspHealthSummaryReport.healthSummary.map(
                                          (item, index) => {
                                            if (index === mainIndex) {
                                              return {
                                                ...item,
                                                healthHistoryComment:
                                                  value || null,
                                              };
                                            }
                                            return item;
                                          },
                                        ),
                                    }),
                                  );
                                }}
                                style={{
                                  width: '840px',
                                }}
                              />
                            </Box>

                            <Box
                              sx={{
                                alignItems: 'center',
                                display: 'inline-flex',
                              }}
                            >
                              <MediumTypography
                                sxProps={{
                                  marginLeft: '4px',
                                  marginRight: '24px',
                                  fontFamily: 'Lato-Regular',
                                  fontSize: 14,
                                  fontWeight: 500,
                                }}
                                labelid={'IFSP.CurrentHealthStatus'}
                                defaultlabel={
                                  'Current Health Status: (include vision and hearing status and oral health)'
                                }
                              />
                            </Box>
                            <Box sx={{ paddingTop: '8px' }}>
                              <CommentTextbox
                                className="translate"
                                name={'concern'}
                                placeholder={'Enter your comments'}
                                rows={5}
                                maxLength={5000}
                                Value={
                                  ifspHealthSummaryReport.healthSummary[
                                    mainIndex
                                  ].currentHealthComment
                                }
                                handlechange={(value) => {
                                  dispatch(
                                    updateIfspHealthSummaryReport({
                                      ...ifspHealthSummaryReport,
                                      healthSummary:
                                        ifspHealthSummaryReport.healthSummary.map(
                                          (item, index) => {
                                            if (index === mainIndex) {
                                              return {
                                                ...item,
                                                currentHealthComment:
                                                  value || null,
                                              };
                                            }
                                            return item;
                                          },
                                        ),
                                    }),
                                  );
                                }}
                                style={{
                                  width: '840px',
                                }}
                              />
                            </Box>

                            <Box
                              sx={{
                                alignItems: 'center',
                                display: 'inline-flex',
                              }}
                            >
                              <MediumTypography
                                sxProps={{
                                  marginLeft: '4px',
                                  marginRight: '24px',
                                  fontFamily: 'Lato-Regular',
                                  fontSize: 14,
                                  fontWeight: 500,
                                }}
                                labelid={'IFSP.AdditionalInformation'}
                                defaultlabel={
                                  'Additional Information: (include relevant information provided by parent interviews and review or records along with reason for referral to EI. )'
                                }
                              />
                            </Box>
                            <Box sx={{ paddingTop: '8px' }}>
                              <CommentTextbox
                                className="translate"
                                name={'concern'}
                                placeholder={'Enter your comments'}
                                rows={5}
                                maxLength={5000}
                                Value={
                                  ifspHealthSummaryReport.healthSummary[
                                    mainIndex
                                  ].additionalInformationComment
                                }
                                handlechange={(value) => {
                                  dispatch(
                                    updateIfspHealthSummaryReport({
                                      ...ifspHealthSummaryReport,
                                      healthSummary:
                                        ifspHealthSummaryReport.healthSummary.map(
                                          (item, index) => {
                                            if (index === mainIndex) {
                                              return {
                                                ...item,
                                                additionalInformationComment:
                                                  value || null,
                                              };
                                            }
                                            return item;
                                          },
                                        ),
                                    }),
                                  );
                                }}
                                style={{
                                  width: '840px',
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ),
                )}
              </Box>
            )}
          </Formik>
          <Box component="div" className="flex__ justifyContent-FlexEnd mb-sm">
            <ButtonComponent
              className="btn-primary btn-cancel"
              labelId={'cancelText'}
              defaultLabelId="Cancel"
              variantType="outlined"
              onClick={() => {
                if (
                  isHealthSummaryChanged ||
                  ifspUnsavedFields.isHealthAndEligibilityFieldsChanged
                ) {
                  setOpenUnSavedModal(true);
                } else {
                  setOpenUnSavedModal(false);
                }
              }}
              disabled={!ifspPermission.hasClientEditPermission}
            />
            <ButtonComponent
              className="btn-primary btn-submit  ml-md"
              labelId="IFSP.SaveHealthAndEligibilitySummary"
              defaultLabelId="Save Health & Eligibility Summary"
              variantType="contained"
              onClick={() => {
                addOrUpdateIfspReportDetails();
              }}
              disabled={!ifspPermission.hasClientEditPermission}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HealthEligibilitySummaryReport;
